import { Check } from 'lucide-react';
import { Button } from './ui/button';

/**
 * 4-column pricing grid with uniform, level rows (11 rows).
 * Every card shows the exact same rows in the exact same sequence so cards
 * line up horizontally — no more uneven layouts.
 */
const PLANS = [
    {
        id: 'starter',
        name: 'Starter',
        price: 29,
        highlight: false,
        managed: true,
    },
    {
        id: 'pro',
        name: 'Pro',
        price: 79,
        highlight: true,
        managed: true,
    },
    {
        id: 'ultimate_byob',
        name: 'Ultimate BYOB',
        price: 149,
        highlight: false,
        managed: false,
    },
    {
        id: 'ultimate_managed',
        name: 'Ultimate Managed',
        price: 249,
        highlight: false,
        managed: true,
    },
];

// 11 rows × 4 columns. `null` = "not applicable" (shown as em-dash).
const FEATURE_MATRIX = [
    {
        label: 'Data Pipeline',
        values: {
            starter: '400 GB / mo Transit',
            pro: '2 TB / mo Transit',
            ultimate_byob: 'Unlimited Transit via Your Bucket',
            ultimate_managed: '5 TB / mo Managed Transit',
        },
    },
    {
        label: 'Active Portals',
        values: {
            starter: 'Max 3 Active Portals',
            pro: 'Max 10 Active Portals',
            ultimate_byob: 'Unlimited Active Portals',
            ultimate_managed: 'Unlimited Active Portals',
        },
    },
    {
        label: 'File Ceiling',
        values: {
            starter: '5 TB Max Per-File Ceiling',
            pro: '5 TB Max Per-File Ceiling',
            ultimate_byob: '5 TB Max Per-File Ceiling',
            ultimate_managed: '5 TB Max Per-File Ceiling',
        },
    },
    {
        label: 'Infrastructure',
        values: {
            starter: 'Managed R2 Storage',
            pro: 'Managed R2 Storage',
            ultimate_byob: 'Bring Your Own Bucket (AWS S3 / R2)',
            ultimate_managed: 'Dedicated Corporate R2 Partition',
        },
    },
    {
        label: 'Data Life',
        values: {
            starter: 'Strict 7-Day Auto-Purge Safety Net',
            pro: 'Strict 7-Day Auto-Purge Safety Net',
            ultimate_byob: 'No 7-Day Purge — Files Live Forever',
            ultimate_managed: 'Strict 7-Day Auto-Purge Safety Net',
        },
    },
    {
        label: 'Rollover Vault',
        values: {
            starter: '1× Plan-Value Rollover Vault',
            pro: '1× Plan-Value Rollover Vault',
            ultimate_byob: null,
            ultimate_managed: '1× Plan-Value Rollover Vault',
        },
    },
    {
        label: 'Loyalty Reward',
        values: {
            starter: '100% Instant Match Upgrade Perk',
            pro: '100% Instant Match Upgrade Perk',
            ultimate_byob: null,
            ultimate_managed: '100% Instant Match Upgrade Perk',
        },
    },
    {
        label: 'Custom Logo & Brand Name Swap',
        values: {
            starter: 'Standard Branding Only',
            pro: 'Standard Branding Only',
            ultimate_byob: 'Full White-Label Logo & Custom Brand Name Swap',
            ultimate_managed: 'Full White-Label Logo & Custom Brand Name Swap',
        },
    },
    {
        label: 'Dynamic Color Engine',
        values: {
            starter: 'Default Orange',
            pro: 'Brand Theme Accent Color Overrides',
            ultimate_byob: 'Brand Theme Accent Color Overrides',
            ultimate_managed: 'Brand Theme Accent Color Overrides',
        },
    },
    {
        label: 'Tailored Greetings',
        values: {
            starter: 'System Default',
            pro: 'Custom Portal Welcome Greetings',
            ultimate_byob: 'Custom Portal Welcome Greetings',
            ultimate_managed: 'Custom Portal Welcome Greetings',
        },
    },
    {
        label: 'Live Interface Preview Sandbox',
        values: {
            starter: null,
            pro: 'Real-Time Dashboard Customizer Preview',
            ultimate_byob: 'Real-Time Dashboard Customizer Preview',
            ultimate_managed: 'Real-Time Dashboard Customizer Preview',
        },
    },
    {
        label: 'Client-Specific Branding',
        values: {
            starter: null,
            pro: null,
            ultimate_byob: 'Custom Logo & Colors per Project Link',
            ultimate_managed: 'Custom Logo & Colors per Project Link',
        },
    },
    {
        label: 'Ecosystem Attribution',
        values: {
            starter: 'Sleek "Powered by Magnidrop" Footer Link',
            pro: 'Sleek "Powered by Magnidrop" Footer Link',
            ultimate_byob: 'Sleek "Powered by Magnidrop" Footer Link',
            ultimate_managed: 'Sleek "Powered by Magnidrop" Footer Link',
        },
    },
];

function RowValue({ value, includedLabel }) {
    if (value === null) {
        return (
            <li className="flex items-start gap-2 text-sm min-h-[2.75rem]">
                <span className="text-muted-foreground/70 mt-0.5 shrink-0 font-mono">—</span>
                <span className="text-muted-foreground/70 italic">Not applicable</span>
            </li>
        );
    }
    return (
        <li className="flex items-start gap-2 text-sm min-h-[2.75rem]">
            <Check className="h-4 w-4 text-[#FF6B35] mt-0.5 shrink-0" />
            <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-0.5">{includedLabel}</p>
                <span className="leading-snug">{value}</span>
            </div>
        </li>
    );
}

export default function PricingTable({ onSelect, betaActive = false, betaFull = false, betaPriceUsd = 15 }) {
    const cta = (planName) => {
        if (betaActive) return `Join Paid Beta ($${betaPriceUsd})`;
        return `Choose ${planName}`;
    };
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-table">
            {PLANS.map((p) => (
                <div
                    key={p.id}
                    data-testid={`pricing-tier-${p.id}`}
                    className={`relative flex flex-col border p-8 ${
                        p.highlight ? 'border-[#FF6B35] shadow-[0_0_0_1px_#FF6B35]' : 'border-border'
                    } bg-card`}
                >
                    {p.highlight && (
                        <span className="absolute -top-3 left-8 bg-[#FF6B35] text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1">
                            Most popular
                        </span>
                    )}
                    <div className="mb-6 min-h-[6.5rem]">
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{p.name}</p>
                        <p className="mt-4 font-heading text-4xl font-medium">${p.price}
                            <span className="text-base text-muted-foreground font-normal">/mo</span>
                        </p>
                        {betaActive && (
                            <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#FF6B35]">
                                Beta window · flat ${betaPriceUsd} entry
                            </p>
                        )}
                    </div>
                    <ul className="space-y-3 flex-1 mb-8" data-testid={`plan-features-${p.id}`}>
                        {FEATURE_MATRIX.map((row) => (
                            <RowValue
                                key={row.label}
                                value={row.values[p.id]}
                                includedLabel={row.label}
                            />
                        ))}
                    </ul>
                    <p className="text-xs text-muted-foreground mb-4 leading-relaxed border-t border-border pt-4 min-h-[3.5rem]">
                        {p.managed
                            ? 'Files automatically wipe after 7 days to keep network pipelines empty and secure.'
                            : 'Files live forever inside your own S3 / R2 bucket — Magnidrop only presigns the transfer route.'}
                    </p>
                    <Button
                        data-testid={`select-plan-${p.id}`}
                        onClick={() => onSelect?.(p.id)}
                        disabled={betaFull}
                        className={p.highlight || betaActive ? 'bg-[#FF6B35] hover:bg-[#E55C2A] text-white' : ''}
                        variant={p.highlight || betaActive ? 'default' : 'outline'}
                    >
                        {betaFull ? 'Beta Full' : cta(p.name)}
                    </Button>
                </div>
            ))}
        </div>
    );
}
